import { useEffect, useRef, useState } from "react"
import {
  createLabel,
  listLabels,
  setRecipeLabels,
  type Label,
} from "@/lib/api"
import { LabelChip, NewLabelChip } from "./LabelChip"

/**
 * LabelEditor — attached labels for a recipe, plus an inline picker.
 *
 * UX
 *  - Default state shows the recipe's current labels as removable chips +
 *    a dashed "+ add label" trigger.
 *  - Clicking the trigger opens a search popover: text input over the
 *    user's vocabulary; tap a chip in the suggestion list to attach. If
 *    the query doesn't match an existing label, a "create '{name}'" row
 *    appears at the bottom — one tap to mint + attach.
 *  - All mutations go through `setRecipeLabels(recipeId, ids[])` — replace
 *    semantics, idempotent, single round-trip per change.
 *  - Local-first: optimistic chip add/remove so the UI doesn't blink.
 *    On error we roll back to the server's truth.
 */
export function LabelEditor({
  recipeId,
  initial,
  onChange,
}: {
  recipeId: string
  initial: Label[]
  onChange?: (labels: Label[]) => void
}) {
  const [attached, setAttached] = useState<Label[]>(initial)
  const [vocab, setVocab] = useState<Label[] | null>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Re-sync from props if the parent reloads the recipe.
  useEffect(() => {
    setAttached(initial)
  }, [initial])

  // Lazy-load vocabulary on first open.
  useEffect(() => {
    if (!open || vocab !== null) return
    listLabels()
      .then(setVocab)
      .catch((e: any) =>
        setErr(e?.response?.data?.error || e?.message || "Couldn't load labels."),
      )
  }, [open, vocab])

  // Close picker on outside-click.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onDoc)
    return () => document.removeEventListener("mousedown", onDoc)
  }, [open])

  async function persist(next: Label[]) {
    setBusy(true)
    setErr(null)
    try {
      const saved = await setRecipeLabels(
        recipeId,
        next.map((l) => l.id),
      )
      setAttached(saved)
      onChange?.(saved)
    } catch (e: any) {
      // Rollback by re-fetching the recipe (caller's responsibility). For
      // now surface the error inline; user can retry.
      setErr(e?.response?.data?.error || e?.message || "Couldn't save labels.")
      setAttached(initial)
    } finally {
      setBusy(false)
    }
  }

  function attach(l: Label) {
    if (attached.some((x) => x.id === l.id)) {
      setOpen(false)
      return
    }
    const next = [...attached, l].sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
    )
    setAttached(next) // optimistic
    void persist(next)
    setOpen(false)
    setQuery("")
  }

  function detach(l: Label) {
    const next = attached.filter((x) => x.id !== l.id)
    setAttached(next) // optimistic
    void persist(next)
  }

  async function mintAndAttach(name: string) {
    setBusy(true)
    setErr(null)
    try {
      const fresh = await createLabel(name)
      // Update vocab so the new chip shows up in the picker next time.
      setVocab((v) => (v ? [...v, fresh] : [fresh]))
      attach(fresh)
    } catch (e: any) {
      setErr(
        e?.response?.data?.error || e?.message || "Couldn't create label.",
      )
    } finally {
      setBusy(false)
    }
  }

  // Picker suggestions = vocab minus already-attached, narrowed by query.
  const q = query.trim().toLowerCase()
  const attachedIds = new Set(attached.map((l) => l.id))
  const suggestions = (vocab ?? []).filter(
    (l) =>
      !attachedIds.has(l.id) &&
      (q === "" || l.name.toLowerCase().includes(q)),
  )
  const exactExists = (vocab ?? []).some(
    (l) => l.name.toLowerCase() === q,
  )
  const canCreate = q.length >= 1 && q.length <= 32 && !exactExists

  return (
    // z-40 on the wrapper lifts the entire LabelEditor (and its absolute
    // popover child) above sibling sections that have no z-index. Without
    // this, the popover got visually clipped behind the macros card on the
    // recipe-detail page once the suggestion list grew tall enough.
    <div ref={containerRef} className="relative z-40">
      <div className="flex flex-wrap items-center gap-1.5">
        {attached.map((l) => (
          <LabelChip
            key={l.id}
            label={l}
            onRemove={() => detach(l)}
          />
        ))}
        <NewLabelChip
          onClick={() => setOpen((v) => !v)}
          label={attached.length === 0 ? "+ add label" : "+"}
        />
        {busy && (
          <span className="font-mono text-[10px] text-muted-foreground ml-1">
            …
          </span>
        )}
      </div>
      {err && (
        <p className="font-sans text-xs text-persimmon mt-2">{err}</p>
      )}

      {open && (
        <div className="absolute z-30 mt-2 w-72 max-w-[calc(100vw-3rem)] bg-surface border border-border rounded shadow-md p-3 animate-fade-up">
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find or create a label"
            className="w-full bg-transparent border-b border-border focus:border-moss focus:outline-none px-0 py-2 font-sans text-sm placeholder:italic placeholder:text-muted-foreground/70"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                if (suggestions.length === 1) {
                  attach(suggestions[0])
                } else if (canCreate) {
                  void mintAndAttach(query.trim())
                }
              } else if (e.key === "Escape") {
                setOpen(false)
              }
            }}
          />
          <div className="mt-3 flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
            {vocab === null ? (
              <span className="font-mono text-[10px] text-muted-foreground">
                loading…
              </span>
            ) : suggestions.length === 0 && !canCreate ? (
              <span className="font-mono text-[10px] text-muted-foreground">
                {vocab.length === 0
                  ? "no labels yet — type one to create"
                  : "no matches"}
              </span>
            ) : (
              <>
                {suggestions.map((l) => (
                  <LabelChip
                    key={l.id}
                    label={l}
                    onClick={() => attach(l)}
                  />
                ))}
                {canCreate && (
                  <button
                    type="button"
                    onClick={() => void mintAndAttach(query.trim())}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-dashed border-moss/60 text-moss hover:bg-moss/[0.04] transition-colors font-mono text-[10px] uppercase tracking-wider"
                  >
                    + create "{query.trim()}"
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
